<script
  setup
  lang="ts"
  generic="T extends TableStats<K>, K extends Record<string, string | number>"
>
import { cn } from 'pejer/src/lib/utils'
import { computed, MaybeRef, unref } from 'vue'
import { TableStats } from '../../types/stats'
import HorizontalLine from '../HorizontalLine.vue'
import { DonutChart } from '../ui/chart-donut'
import SectionCardHeader from '../ui/section-card/SectionCardHeader.vue'

type DataOptions = {
  key: keyof T
  name: string
  color?: string
  icon?: string
  formatter?: (value: number) => number
}

const props = defineProps<{
  class?: any
  dataOptions: DataOptions[]
  data: MaybeRef<T[]>
  isMoney?: boolean
  horizontal?: boolean
  title?: string
}>()

const dataFormatted = computed(() => {
  const dataRef = unref(props.data)
  // First we want to reduce the data to get totals for each category for all properties that are of type number
  const data = (dataRef ?? []).reduce(
    (acc, item) => {
      props.dataOptions.forEach((option) => {
        const formatted = option.formatter
          ? option.formatter(item[option.key] as number)
          : item[option.key]
        if (typeof item[option.key] === 'number') {
          acc[option.key] = ((acc[option.key] as number) || 0) + (formatted as number)
        }
      })
      return acc
    },
    {} as Record<keyof T, number>
  )

  // Secondly
  return props.dataOptions.map((option) => ({
    name: option.name,
    total: data[option.key] ?? 0,
    predicted: data[option.key] ?? 0,
  }))
})

const colors = computed(() => props.dataOptions.map((option) => option.color || '#6366F1'))

function valueFormatter(tick: number | Date) {
  if (props.isMoney && typeof tick === 'number')
    return new Intl.NumberFormat('no-NO', { style: 'currency', currency: 'NOK' }).format(
      tick as number
    )

  return typeof tick === 'number' ? `${new Intl.NumberFormat('no-NO').format(tick).toString()}` : ''
}
</script>
<template>
  <!-- <DataStateDisplay v-bind="dataState"> -->
  <div
    :class="
      cn(
        'grid items-center gap-2 w-full',
        {
          'grid-cols-1': !props.horizontal,
          'grid-cols-2': props.horizontal,
        },
        props.class
      )
    "
  >
    <DonutChart
      index="name"
      :category="'total'"
      :data="dataFormatted"
      :value-formatter="valueFormatter"
      :show-legend="true"
      :colors="colors"
      class="w-full"
    />
    <div class="flex flex-1 w-full flex-col gap-1 text-sm p-2 mt-2">
      <div class="mb-1" v-if="title">
        <SectionCardHeader>{{ title }}</SectionCardHeader>
        <HorizontalLine class="border-b-2 border-slate-300 rounded" />
      </div>

      <div
        v-for="(item, index) in dataFormatted"
        :key="item.name"
        class="flex items-center justify-between gap-2"
      >
        <span class="font-semibold flex items-center gap-2"
          ><div
            class="w-3 h-3 rounded-sm shrink-0"
            :style="{
              'background-color': colors[index % colors.length],
            }"
          ></div>
          {{ item.name }}
        </span>
        <span class="text-muted-foreground">{{ valueFormatter(item.total as number) }}</span>
      </div>
    </div>
  </div>
  <!-- </DataStateDisplay> -->
</template>
